import styles from "./TimeSlotSelector.module.scss";

import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import calendarIconUrl from "../../svgs/calendar.svg";
import {
  groupSlotsByTimeOfDay,
  TimeOfDay,
} from "../../utils/groupSlotsByTimeOfDay";
import { TimeSlot } from "../TimeSlot/TimeSlot";

const DEFAULT_TIMEZONE = "UTC";

const SECTION_ORDER = [
  "morning",
  "afternoon",
  "evening",
] as const satisfies readonly TimeOfDay[];

type TimeSlotSelectorProps = {
  slots: string[];
  timezone?: string;
  onSelected?: (slot: string) => void;
};

export function TimeSlotSelector({
  slots,
  timezone = DEFAULT_TIMEZONE,
  onSelected,
}: TimeSlotSelectorProps) {
  const { t } = useTranslation();

  const handleOnSelected = useCallback(
    (slot: string) => {
      onSelected?.(slot);
    },
    [onSelected],
  );

  const groupedSlots = useMemo(
    () => groupSlotsByTimeOfDay(slots, timezone),
    [slots, timezone],
  );

  if (!slots.length) {
    return (
      <div className={styles.emptyContainer}>
        <img src={calendarIconUrl} alt="Calendar" />
        <span className={styles.emptyLabel}>
          {t("book.slotSelector.noTimesInDate")}
        </span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {SECTION_ORDER.map((section) => {
        const sectionSlots = groupedSlots[section];
        if (!sectionSlots.length) return null;

        return (
          <div key={section} className={styles.section}>
            <h3 className={styles.sectionLabel}>
              {t(`bookingWidget.timesOfDay.${section}`)}
            </h3>
            <div className={styles.grid}>
              {sectionSlots.map((slot) => (
                <TimeSlot
                  key={slot}
                  slot={slot}
                  timezone={timezone}
                  onSelected={handleOnSelected}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
